/**
 * ### insertLineBreak
 *
 * @description
 * Inserts a line break (\n) at the current cursor position in a contentEditable element
 * and positions the cursor after the line break. Uses a temporary zero-width space
 * for reliable cursor positioning, which is removed after positioning.
 *
 * @example
 * const success = insertLineBreak(spanRef.current);
 * if (success) {
 *   // Line break was inserted successfully
 * }
 *
 * @param element - The contentEditable element to insert line break into
 * @returns Boolean indicating if line break was successfully inserted
 */
const insertLineBreak = (element: HTMLSpanElement | null): boolean => {
  if (!element) return false;

  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return false;

  const range = selection.getRangeAt(0);

  // Delete any selected content first
  range.deleteContents();

  // Create temporary text node with character to establish reliable cursor target
  const tempTextNode = document.createTextNode('\u200B'); // Zero-width space (invisible)
  range.insertNode(tempTextNode);

  // Insert line break before the temporary text node
  const lineBreakNode = document.createTextNode('\n');
  range.insertNode(lineBreakNode);

  // Position cursor at the temporary text node (now after the line break)
  range.setStart(tempTextNode, 0);
  range.setEnd(tempTextNode, 0);

  selection.removeAllRanges();
  selection.addRange(range);

  return true;
};

export default insertLineBreak;
